// src/pages/Payment/CancelPage.jsx
// ❌ Página de pagamento cancelado
// Criado em: 16/03/2026
// Versão: 1.0

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  XCircle, 
  ArrowLeft, 
  HelpCircle, 
  MessageCircle,
  RefreshCw,
  Clock
} from 'lucide-react';

/**
 * Página quando o usuário cancela o pagamento
 * Oferece alternativas e suporte
 */
export default function PaymentCancelPage() {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(30);

  // ============================================
  // ⏱️ CONTADOR PARA REDIRECIONAMENTO
  // ============================================
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/planos');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  // ============================================
  // 🎯 AÇÕES
  // ============================================

  const handleTryAgain = () => {
    navigate('/planos');
  };

  const handleContactSupport = () => {
    window.open('https://wa.me/5511999999999?text=Preciso%20de%20ajuda%20com%20o%20pagamento', '_blank');
  };

  const handleBackToPlans = () => {
    navigate('/planos');
  };

  // ============================================
  // 🎨 RENDERIZAÇÃO
  // ============================================
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      
      {/* Card principal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden"
      >
        
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-8 text-center relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full" />
          <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-white/10 rounded-full" />
          
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
            className="w-20 h-20 bg-white rounded-full mx-auto mb-4 flex items-center justify-center shadow-xl"
          >
            <XCircle className="w-10 h-10 text-amber-600" />
          </motion.div>
          
          <h1 className="text-2xl font-bold text-white mb-2">
            Pagamento não concluído
          </h1>
          <p className="text-amber-100">
            O processo foi cancelado
          </p>
        </div>

        {/* Conteúdo */}
        <div className="p-8">
          
          {/* Mensagem principal */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-center mb-6"
          >
            <p className="text-slate-600">
              Nenhum valor foi cobrado. Se você encontrou alguma dificuldade, 
              estamos aqui para ajudar!
            </p>
          </motion.div>

          {/* Possíveis motivos */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="bg-slate-50 rounded-xl p-4 mb-6"
          >
            <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <HelpCircle className="w-4 h-4" />
              Possíveis motivos:
            </h3>
            
            <ul className="space-y-2 text-sm text-slate-600">
              <li className="flex items-center gap-2">
                <div className="w-1 h-1 bg-slate-400 rounded-full" />
                Você cancelou voluntariamente
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1 h-1 bg-slate-400 rounded-full" />
                Dados do cartão inválidos
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1 h-1 bg-slate-400 rounded-full" />
                Saldo insuficiente
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1 h-1 bg-slate-400 rounded-full" />
                Tempo de sessão expirado
              </li>
            </ul>
          </motion.div>

          {/* Ações */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="space-y-3"
          >
            <button
              onClick={handleTryAgain}
              className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Tentar novamente
            </button>
            
            <button
              onClick={handleContactSupport}
              className="w-full py-3 border border-green-200 bg-green-50 text-green-700 rounded-xl font-semibold hover:bg-green-100 transition-all flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-4 h-4" />
              Falar com suporte
            </button>
            
            <button
              onClick={handleBackToPlans}
              className="w-full py-3 text-slate-600 hover:text-slate-900 transition-colors flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar para os planos
            </button>
          </motion.div>

          {/* Redirecionamento automático */}
          <div className="mt-6 pt-4 border-t border-slate-100">
            <p className="text-center text-xs text-slate-400 flex items-center justify-center gap-1">
              <Clock className="w-3 h-3" />
              Redirecionando para planos em {countdown}s
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}